import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription as UiFormDescription,
} from '@/components/ui/form';

import { createProjectSchema, type CreateProjectFormValues } from '@/schemas';
import type { CreateProjectCardProps } from '@/types';
import { toast } from 'sonner';
import {
  useCreateExternalProjectMutation,
  useCreateInternalProjectMutation,
  useUpdateExternalProjectMutation,
  useUpdateInternalProjectMutation,
} from '@/state';
import { useTenant } from '@/hooks';
import { getMessageByStatus } from '@/lib';

export const ProjectCuHandler: React.FC<CreateProjectCardProps> = ({
  projectType,
  open,
  setOpen,
  title,
  project,
  openFor,
  setOpenFor,
  onCreated,
}) => {
  if (openFor === '') return null;
  const { tenant } = useTenant();

  const [createInternalProject] = useCreateInternalProjectMutation();
  const [updateInternalProject] = useUpdateInternalProjectMutation();
  const [createExternalProject] = useCreateExternalProjectMutation();
  const [updateExternalProject] = useUpdateExternalProjectMutation();

  // 2 = Individual, 1 = Group/Cohort
  // Default is 2 => Individual
  const [projectStructure, setProjectStructure] = React.useState<1 | 2>(2);

  const form = useForm({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: '',
      durationDays: 30,
    } as CreateProjectFormValues,
    mode: 'onChange',
  });

  const { isDirty } = form.formState;

  React.useEffect(() => {
    if (project) {
      form.reset({
        name: project.name,
        durationDays: project.project_duration,
      });
      setProjectStructure(project.project_structure);
    } else {
      setProjectStructure(1);
    }
  }, [project]);

  const name = form.watch('name');
  const duration = form.watch('durationDays');

  const previewName = useMemo(
    () => (name?.trim() ? name.trim() : 'Untitled project'),
    [name]
  );

  const previewDuration = useMemo(() => {
    const n = Number(duration || 0);
    if (!Number.isFinite(n) || n <= 0) return 'Duration not set';
    return `${n} day${n === 1 ? '' : 's'}`;
  }, [duration]);

  const closeAndReset = useCallback(() => {
    setOpen(false);
    form.reset({ name: '', durationDays: 30 });
    setProjectStructure(2);
  }, [form, setOpen]);

  const createProjectHandler = useCallback(
    async (data: CreateProjectFormValues) => {
      try {
        if (project) {
          toast.error('Project already exists pleased refresh you page');
          return;
        }
        if (projectType === 'INTERNAL') {
          const createdProject = await createInternalProject({
            tenant: tenant!,
            data: {
              name: data.name,
              project_duration: data.durationDays,
              project_structure: 1,
              project_type: null,
            },
          }).unwrap();
          await onCreated?.(createdProject.project_id ?? createdProject.id);

          window.dispatchEvent(
            new CustomEvent('switch-project-tab', {
              detail: 'internal',
            })
          );

          toast.success('Project created successfully');
        } else if (projectType === 'EXTERNAL') {
          const createdProject = await createExternalProject({
            tenant: tenant!,
            data: {
              name: data.name,
              project_duration: data.durationDays,
              // use chosen structure for external projects
              project_structure: projectStructure, // default is 2 
              project_type: 2, // default is 2 
            },
          }).unwrap();
          await onCreated?.(createdProject.project_id ?? createdProject.id);

          window.dispatchEvent(
            new CustomEvent('switch-project-tab', {
              detail: 'external',
            })
          );

          toast.success('Project created successfully');
        }
      } catch (error: any) {
        toast.error(`Error`, {
          description: getMessageByStatus(error?.status || 500),
        });
      } finally {
        closeAndReset();
        setOpen(false);
        setOpenFor('');
      }
    },
    [projectType, closeAndReset, project, setOpen, projectStructure, onCreated, tenant, createInternalProject, createExternalProject, setOpenFor]
  );

  const updateProjectHandler = useCallback(
    async (data: CreateProjectFormValues) => {
      try {
        if (!project) {
          toast.error('Project not selected');
          return;
        }

        if (projectType === 'INTERNAL') {
          await updateInternalProject({
            tenant: tenant!,
            data: {
              name: data.name,
              project_duration: data.durationDays,
              project_structure: 1,
              project_type: null,
            },
            projectId: project.id,
          }).unwrap();
          toast.success('Project updated successfully');
        } else if (projectType === 'EXTERNAL') {
          const externalProjectId = project.project_id ?? project.id;
          await updateExternalProject({
            tenant: tenant!,
            data: {
              name: data.name,
              project_duration: data.durationDays,
              project_structure: projectStructure,
              board: null,
            },
            projectId: externalProjectId,
            resourceId: project.project_id ? project.id : undefined,
          }).unwrap();
          toast.success('Project updated successfully');
        }
      } catch (error: any) {
        toast.error(`Error`, {
          description: getMessageByStatus(error?.status || 500),
        });
      } finally {
        closeAndReset();
        setOpen(false);
        setOpenFor('');
      }
    },
    [closeAndReset, projectType, project, setOpenFor, projectStructure]
  );

  const isSubmitting = form.formState.isSubmitting;
  const canSubmit = form.formState.isValid && !isSubmitting;

  // treat structure change as "dirty" too (only for external update)
  const structureChanged =
    !!project &&
    projectType === 'EXTERNAL' &&
    openFor === 'update' &&
    projectStructure !== project.project_structure;

  const canSubmitWithStructure = canSubmit && (isDirty || structureChanged);

  return (
    <>
      {/* dialog */}
      <Dialog
        open={openFor === 'create' || openFor === 'update' ? open : false}
        onOpenChange={
          openFor === 'create' || openFor === 'update' ? setOpen : undefined
        }
      >
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {title || 'Create a new project'}
            </DialogTitle>
            <DialogDescription>
              {!title &&
                ' Add a name and duration. You can fill out the rest later.'}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={
                project && openFor === 'update'
                  ? form.handleSubmit(updateProjectHandler)
                  : form.handleSubmit(createProjectHandler)
              }
              className="space-y-6"
            >
              {/* Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g., Marketing Campaign 2026"
                        autoFocus
                      />
                    </FormControl>
                    <UiFormDescription>
                      Keep it short and clear (3–80 characters).
                    </UiFormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Duration + Preview */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="durationDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={365}
                          placeholder='Enter duration'
                          defaultValue={(field.value as number) ?? 30}
                          step={1}
                          onChange={(e) => {
                            // allow empty -> still keep form stable
                            const v = e.target.value;
                            field.onChange(v === '' ? undefined : Number(v));
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <UiFormDescription>
                        Total project length in days.
                      </UiFormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="rounded-2xl border p-4">
                  <p className="text-sm text-muted-foreground">Live preview</p>
                  <p className="mt-2 truncate text-base font-semibold">
                    {previewName}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {previewDuration}
                  </p>

                  <div className="mt-4 rounded-xl border bg-muted/40 p-3">
                    <p className="text-xs text-muted-foreground">Tip</p>
                    <p className="text-sm">
                      Use a name that matches the board/project goal, e.g.
                      “Design Sprint – Week 1”.
                    </p>
                  </div>

                  {/* Project structure toggle for EXTERNAL projects */}
                  {projectType === 'EXTERNAL' && openFor === 'update' && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">
                        Project structure
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            projectStructure === 2 ? 'default' : 'outline'
                          }
                          className="flex-1 justify-start gap-2 pt-6 pb-6"
                          onClick={() => setProjectStructure(2)}
                        >
                          <span className="text-lg">👤</span>
                          <span className="flex flex-col text-left">
                            <span className="text-xs font-medium bg:text-white">
                              Individual project
                            </span>
                            <span className="text-[11px] bg:text-white">
                              Assigned to a single learner.
                            </span>
                          </span>
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant={
                            projectStructure === 1 ? 'default' : 'outline'
                          }
                          className="flex-1 justify-start gap-2 pt-6 pb-6"
                          onClick={() => setProjectStructure(1)}
                        >
                          <span className="text-lg">👥</span>
                          <span className="flex flex-col text-left">
                            <span className="text-xs font-medium bg:text-white">
                              Group / cohort project
                            </span>
                            <span className="text-[11px] bg:text-white">
                              Shared across multiple learners.
                            </span>
                          </span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={closeAndReset}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmitWithStructure}
                >
                  {isSubmitting
                    ? !project
                      ? 'Creating...'
                      : 'Updating...'
                    : !project
                      ? 'Create project'
                      : 'Update project'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
